import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';
import { jwtDecode } from 'jwt-decode';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import LocationInputGroup from '../../components/Form/LocationInputGroup';

import { 
    Plus, Edit, Trash2, Search, CheckCircle, Clock, AlertCircle, ArrowLeft, MapPin,
    GripVertical, LayoutTemplate, Rows, ClipboardList,
    ChevronRight, Settings, UserPlus, MessageSquare, Megaphone, DollarSign, Save, X
} from 'lucide-react';

// Import Modals (Assuming these paths are correct in your project)
import PhaseModal from '../../components/Projects/Modals/PhaseModal';
import TaskModal from '../../components/Projects/Modals/TaskModal';
import FindContractorsModal from '../../components/Projects/Modals/FindContractorsModal';
import BrowseProsModal from '../../components/Projects/Modals/BrowseProsModalUpdated';

import './_AIProjectManager.css';

// ==================================================================================
// --- INTERNAL COMPONENT: Edit Project Modal ---
// ==================================================================================
const EditProjectModal = ({ isOpen, onClose, onSave, project }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [budget, setBudget] = useState('');
    
    // Location State
    const [postcode, setPostcode] = useState('');
    const [suburb, setSuburb] = useState('');
    const [state, setState] = useState('');
    const [address, setAddress] = useState(''); // Add address state
    const [locationDisplay, setLocationDisplay] = useState('');

    useEffect(() => {
        if (project) {
            setName(project.name || '');
            setDescription(project.description || '');
            setBudget(project.budget || project.Budget || '');
            
            setPostcode(project.postcode || '');
            setSuburb(project.suburb || '');
            setState(project.myState || project.state || ''); // Handle backend naming (MyState) vs frontend (state)
            setAddress(project.address || project.Address || ''); // Initialize address

            // Construct a display string for the autocomplete input if we have data
            if (project.address || project.Address) {
                setLocationDisplay(project.address || project.Address);
            } else if (project.suburb && (project.myState || project.state)) {
                setLocationDisplay(`${project.suburb}, ${project.myState || project.state} ${project.postcode || ''}`);
            } else {
                setLocationDisplay(project.postcode || '');
            }
        }
    }, [project]);

    const handleLocationSelect = (loc) => {
        setPostcode(loc.postcode || '');
        setSuburb(loc.suburb || '');
        setState(loc.state || '');
        setAddress(loc.fullAddress || ''); // Update address
        setLocationDisplay(loc.fullAddress || '');
    };

    const handleSaveClick = () => {
        if (name.trim().length < 3) {

            // alert("Project name must be at least 3 characters.");
            showToast('error', 'Project name must be at least 3 characters.');

            return;
        }
        // Pass all data back to parent
        onSave({
            name,
            description,
            budget,
            postcode,
            suburb,
            state, // This maps to MyState in backend
            address // Pass address
        });
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>Edit Project Details</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
                </div>

                <div className="form-group">
                    <label>Project Name <span style={{color:'red'}}>*</span></label>
                    <input 
                        type="text" 
                        value={name} 
                        minLength={3}
                        onChange={(e) => setName(e.target.value)} 
                        className="modal-input"
                    />
                </div>

                <div className="form-group">
                    <label>Description</label>
                    <textarea 
                        rows={3}
                        value={description} 
                        onChange={(e) => setDescription(e.target.value)} 
                        className="modal-input"
                    />
                </div>

                {/* Location and Budget Row */}
                <div className="form-row" style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                    <div className="form-group half" style={{ flex: 1 }}>
                        <LocationInputGroup 
                            label="Location"
                            value={locationDisplay} 
                            onLocationSelect={handleLocationSelect}
                            suburb={suburb}
                            state={state}
                        />     
                    </div>

                    <div className="form-group half" style={{ flex: 1 }}>
                        <label style={{fontWeight:600, fontSize:'0.9rem', color:'#374151'}}>Estimated Budget</label>
                        <div className="input-with-icon" style={{ 
                            display: 'flex', alignItems: 'center', position: 'relative', 
                            border: '1px solid #ced4da', borderRadius: '6px', background: 'white' 
                        }}>
                            <div style={{ padding: '0 10px', color: '#6c757d' }}>
                                <DollarSign size={16} />
                            </div>
                            <input 
                                type="number" 
                                placeholder="0.00" 
                                value={budget}
                                onChange={(e) => setBudget(e.target.value)}
                                style={{ border: 'none', boxShadow: 'none', width: '100%', paddingLeft: '0' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                    <button onClick={handleSaveClick} className="save-button">Save Changes</button>
                    <button onClick={onClose} className="cancel-button">Cancel</button>
                </div>
            </div>
        </div>
    );
};


// ==================================================================================
// --- HELPER: Job Status Widget ---
// ==================================================================================

const BroadcastIndicator = ({ task }) => {
    if (task.jobId && (task.status === 'Open' || task.status === 'Active')) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: '4px', 
                fontSize: '0.7rem', color: '#16A34A', marginTop: '4px', fontWeight: 500
            }}>
                <Megaphone size={12} /> Contractors Notified
            </div>
        );
    }
    return null;
};

const JobStatusWidget = ({ task }) => {
    const navigate = useNavigate();
    const [inviteCount, setInviteCount] = useState(0);
    const [quoteCount, setQuoteCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const jobId = task?.jobId; 

    useEffect(() => {
        if (!jobId) { setLoading(false); return; }

        const fetchData = async () => {
            try {
                const rawUserType = localStorage.getItem('userType') || 'HomeUser';
                let userId = 0;
                let apiUserType = "HomeUser";

                if (rawUserType === "Home" || rawUserType === "HomeUser") {
                    userId = parseInt(localStorage.getItem('loggedInHomeUserId') || '0', 10);
                } else {
                    apiUserType = "BusinessUser";
                    userId = parseInt(localStorage.getItem('loggedInBusinessId') || '0', 10);
                }

                if (!userId) return;

                const [leadsRes, quotesRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/Jobs/getjobleads?userId=${userId}&userType=${apiUserType}`),
                    axios.get(`${API_BASE_URL}/api/Jobs/quote/${jobId}`).catch(() => ({ data: [] }))
                ]);

                const rawLeads = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.result || []);
                const taskLeads = rawLeads.filter(l => String(l.jobId || l.JobId) === String(jobId));

                setInviteCount(taskLeads.length);
                setQuoteCount(quotesRes.data.length);

            } catch (err) {
                console.error("Failed to load widget stats", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [jobId]);

    if (!jobId) return null; 

    const goToConsole = (e) => {
        e.stopPropagation();
        navigate(`/job-console/${jobId}`);
    };

    if (loading) return <span style={{fontSize:'0.75rem', color:'#adb5bd'}}>Loading...</span>;

    return (
        <div className="job-status-widget-compact" style={{gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
            <div 
                onClick={goToConsole}
                style={{
                    display:'flex', alignItems:'center', gap:'4px', 
                    fontSize:'0.75rem', padding:'4px 8px', 
                    background:'#e7f0ff', color:'#0052cc', 
                    borderRadius:'20px', cursor:'pointer', fontWeight: 600,
                    border: '1px solid #b3d1ff', whiteSpace: 'nowrap'
                }}
                title="View Invites in Console"
            >
                <UserPlus size={12} />
                {inviteCount} Invites
            </div>

            <div 
                onClick={goToConsole}
                style={{
                    display:'flex', alignItems:'center', gap:'4px', 
                    fontSize:'0.75rem', padding:'4px 8px', 
                    background: quoteCount > 0 ? '#d1e7dd' : '#f8f9fa', 
                    color: quoteCount > 0 ? '#0f5132' : '#6c757d', 
                    borderRadius:'20px', cursor:'pointer', fontWeight: 600,
                    border: quoteCount > 0 ? '1px solid #badbcc' : '1px solid #dee2e6', whiteSpace: 'nowrap'
                }}
                title="View Quotes in Console"
            >
                <MessageSquare size={12} />
                {quoteCount} Quotes
            </div>
            
            <button 
                className="icon-btn-circle" 
                style={{width: '28px', height: '28px'}}
                title="Manage Job" 
                onClick={goToConsole}
            >
                <Settings size={14} />
            </button>
        </div>
    );
};

// ==================================================================================
// --- COMPONENT: Horizontal Task ---
// ==================================================================================
// ==================================================================================
// --- COMPONENT: Horizontal Task ---
// ==================================================================================
const HorizontalTask = ({ task, onEdit, onDelete, onFindContractors }) => {
    const navigate = useNavigate();
    const hasJob = task.jobId && (task.status === 'Open' || task.status === 'Active' || task.status === 'Assigned' || task.status === 'Completed');

    const handleTitleClick = () => {
        if (hasJob) navigate(`/job-console/${task.jobId}`);
    };

    let statusIcon = <AlertCircle size={16} color="#ffc107" />;
    let statusClass = "status-pending";

    if (task.status === 'Open') {
        statusIcon = <Megaphone size={16} color="#16A34A" />;
        statusClass = "status-active";
    } else if (task.status === 'Assigned' || task.status === 'Active') {
        statusIcon = <Clock size={16} color="#007bff" />;
        statusClass = "status-active";
    } else if (task.status === 'Completed') {
        statusIcon = <CheckCircle size={16} color="#28a745" />;
        statusClass = "status-completed";
    }

    return (
        <div className="task-card-vertical" data-id={task.id}>
            {/* 
                FIX APPLIED: 
                1. Added alignItems: 'flex-start' to align items to top
                2. Added gap to separate drag handle, content, and actions 
            */}
            <div className="task-top-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                
                <GripVertical className="drag-handle" size={20} style={{ flexShrink: 0, marginTop: '4px', color: '#9ca3af', cursor:'grab' }} />
                
                {/* FIX: Added flex: 1 and minWidth: 0 to force text wrapping so it doesn't overlap actions */}
                <div className="task-info" style={{ flex: 1, minWidth: 0 }}>
                    <h4 
                        onClick={handleTitleClick}
                        style={{
                            cursor: hasJob ? 'pointer' : 'default', 
                            textDecoration: hasJob ? 'underline' : 'none',
                            textDecorationColor: '#cbd5e1',
                            color: hasJob ? '#0F172A' : 'inherit',
                            margin: '0 0 4px 0', // Ensure spacing below title
                            wordWrap: 'break-word' // Prevent long words breaking layout
                        }}
                    >
                        {task.name}
                    </h4>
                    <p style={{ margin: 0 }}>{task.description}</p>
                    <BroadcastIndicator task={task} />
                </div>
                
                {/* FIX: Moved Status Badge here and grouped with Actions. 
                    This ensures it sits to the right and never overlaps text. */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                    
                    {/* Status Badge with explicit styling to prevent overlap */}
                    <span className={`status-badge ${statusClass}`} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px', 
                        fontSize: '0.7rem', 
                        padding: '2px 8px',
                        whiteSpace: 'nowrap',
                        position: 'static' // Override any potential absolute positioning
                    }}>
                        {statusIcon} {task.status || "Pending"}
                    </span>

                    <div className="task-actions" style={{ marginTop: '0' }}>
                        <button onClick={() => onEdit(task)} className="icon-button"><Edit size={14} /></button>
                        <button onClick={() => onDelete(task.id, task.phaseId)} className="icon-button delete-button"><Trash2 size={14} /></button>
                    </div>
                </div>
            </div>
            
            <div className="task-meta" style={{ marginTop: '10px' }}>
                <span className="trade-tag">{task.profession?.name || task.suggestedTrade || "General"}</span>
                {/* REMOVED: Status badge was here, causing the layout conflict. Moved to top row. */}
            </div>

            <div className="task-footer">
                {!task.jobId ? (
                    <button className="find-contractors-btn-compact" onClick={() => onFindContractors(task)}>
                        <Search size={14} /> Find Contractors
                    </button>
                ) : (
                    <div style={{width: '100%', marginTop: '5px'}}>
                        <JobStatusWidget task={task} />
                    </div>
                )}
            </div>
        </div>
    );
};

// --- VERTICAL TASK ---
const VerticalTask = ({ task, onEdit, onDelete, onFindContractors }) => {
    const navigate = useNavigate();
    const hasJob = task.jobId && (task.status === 'Open' || task.status === 'Active' || task.status === 'Assigned');

    const handleTitleClick = () => {
        if (hasJob) navigate(`/job-console/${task.jobId}`);
    };

    return (
        <div className="task-card-wide" data-id={task.id}>
            <div className="task-info-wide">
                <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                    <GripVertical className="drag-handle-vertical" size={18} style={{cursor:'grab', color:'#ccc'}} />
                    <div style={{flex:1}}>
                        <h4>
                            <span 
                                onClick={handleTitleClick}
                                style={{
                                    cursor: hasJob ? 'pointer' : 'default', 
                                    color: hasJob ? '#2563EB' : 'inherit',
                                    fontWeight: hasJob ? '700' : '600'
                                }}
                            >
                                {task.name} 
                            </span>
                            <Edit size={12} className="mini-action-btn" onClick={() => onEdit(task)}/>
                            <Trash2 size={12} className="mini-action-btn" onClick={() => onDelete(task.id, task.phaseId)} style={{color:'red'}}/>
                        </h4>
                        
                        <p>{task.profession?.name || task.suggestedTrade || "General"} • {task.description}</p>
                        <BroadcastIndicator task={task} />
                    </div>
                </div>
            </div>

            <div className="task-action-area-vertical">
                {!task.jobId ? (
                    <button className="find-contractors-btn-compact" style={{width: '100%', display: 'flex', justifyContent: 'center', gap: '8px'}} onClick={() => onFindContractors(task)}>
                        <Search size={14} /> Find Contractors
                    </button>
                ) : (
                    <JobStatusWidget task={task} />
                )}
            </div>
        </div>
    );
};


// ==================================================================================
// --- COMPONENT: Horizontal Phase ---
// ==================================================================================
const HorizontalPhase = ({ phase, setTasksForPhase, onAddTask, onEditPhase, onDeletePhase, onEditTask, onDeleteTask, onFindContractors, onTaskDragEnd }) => {
    const tasks = Array.isArray(phase.tasks) ? phase.tasks : [];

    return (
        <div className="phase-column" data-phase-id={phase.id}>
            <div className="phase-header">
                 <div className="phase-title-group" style={{display:'flex', alignItems:'center', flexGrow:1, color: 'black'}}>
                     <GripVertical className="drag-handle" size={20} />
                     <h3>{phase.name}</h3>
                </div>
                <div className="phase-actions">
                    <button onClick={() => onEditPhase(phase)} className="icon-button"><Edit size={16} /></button>
                    <button onClick={() => onDeletePhase(phase.id)} className="icon-button delete-button"><Trash2 size={16} /></button>
                </div>
            </div>

            <ReactSortable
                list={tasks}
                setList={(newTasks) => setTasksForPhase(phase.id, newTasks)}
                group="shared-tasks"
                animation={150}
                ghostClass="sortable-ghost"
                chosenClass="sortable-chosen"
                dragClass="sortable-drag"
                handle=".drag-handle"
                className="task-list"
                delay={10} 
                // FIX 1: Add onEnd to trigger API call
                // onEnd={() => onTaskDragEnd(phase.id)}
                onEnd={(evt) => {

                    // destination phase id
                    const destinationPhaseId = Number(
                        evt.to.closest(".phase-column")?.dataset?.phaseId
                    );

                    // source phase id
                    const sourcePhaseId = Number(
                        evt.from.closest(".phase-column")?.dataset?.phaseId
                    );

                    onTaskDragEnd(destinationPhaseId);
                }}
            >
                {tasks.map((task) => (
                    <HorizontalTask
                        key={task.id}
                        task={task}
                        onEdit={onEditTask}
                        onDelete={onDeleteTask}
                        onFindContractors={onFindContractors}
                    />
                ))}
            </ReactSortable>

            <button className="add-task-btn" onClick={() => onAddTask(phase.id)}>
                <Plus size={16} /> Add Task
            </button>
        </div>
    );
};

// ==================================================================================
// --- MAIN PAGE: ProjectDetail ---
// ==================================================================================
const ProjectDetail = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    
    // Core Data State
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [username, setUsername] = useState('');
    const [viewMode, setViewMode] = useState(() => window.innerWidth <= 768 ? 'vertical' : 'horizontal');
    const [homeUserId, setHomeUserId] = useState(null);

    // CRUD Modals State
    const [isPhaseModalOpen, setIsPhaseModalOpen] = useState(false);
    const [currentPhase, setCurrentPhase] = useState(null);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [currentPhaseIdForTask, setCurrentPhaseIdForTask] = useState(null);
    
    // FIX 2: Project Edit Modal State
    const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);

    // Contractor Modals State
    const [isFindContractorsModalOpen, setIsFindContractorsModalOpen] = useState(false);
    const [isBrowseProsModalOpen, setIsBrowseProsModalOpen] = useState(false);
    const [activeTaskForContractors, setActiveTaskForContractors] = useState(null);

    // Ref for drag and drop state consistency
    const projectRef = useRef(project);
    useEffect(() => { projectRef.current = project; }, [project]);

    // --- 1. INITIALIZATION ---
    useEffect(() => {
        const token = localStorage.getItem('token');
        let uid = null;
        let uname = '';
        if (token) {
          try {
            const decodedToken = jwtDecode(token);
            uname = decodedToken.UserName || localStorage.getItem('loggedInUsername') || 'User';
            uid = decodedToken.nameid || localStorage.getItem('loggedInHomeUserId');
          } catch (e) {
            console.warn("JWT decode failed, falling back to storage:", e);
            uname = localStorage.getItem('loggedInUsername') || 'User';
            uid = localStorage.getItem('loggedInHomeUserId');
          }
        } else {
          uname = localStorage.getItem('loggedInUsername') || '';
          uid = localStorage.getItem('loggedInHomeUserId');
        }
        if (uname) setUsername(uname);
        if (uid) setHomeUserId(uid);
        if (!uid) setError("No user identified. Please log in.");
    }, []);

    useEffect(() => {
        if (projectId) fetchProject();
    }, [projectId]);

    const fetchProject = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/UserProjects/${projectId}`);
            const projectData = response.data;
            
            // Normalize data structure
            if (projectData.phases) {
                projectData.phases = projectData.phases.map(p => ({
                    ...p,
                    id: p.phaseId || p.id,
                    tasks: (p.tasks || []).map(t => ({
                        ...t,
                        id: t.taskId || t.id,
                        phaseId: p.phaseId || p.id
                    })).sort((a, b) => a.order - b.order)
                })).sort((a, b) => a.order - b.order);
            } else {
                projectData.phases = [];
            }
            setProject(projectData);
        } catch (err) {
            setError('Failed to fetch project data.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // --- 2. DRAG AND DROP HANDLERS ---
    const handleSetPhases = (newPhases) => {
        setProject(prev => ({ ...prev, phases: newPhases }));
    };

    const handleSetTasksForPhase = (phaseId, newTasks) => {
        setProject(prev => {
            const newPhases = prev.phases.map(p => {
                if (p.id === phaseId) {
                    const updatedTasks = newTasks.map(t => ({...t, phaseId: phaseId}));
                    return { ...p, tasks: updatedTasks };
                }
                return p;
            });
            return { ...prev, phases: newPhases };
        });
    };

    const onEndPhaseDrag = async (evt) => {
        setTimeout(async () => {
            const currentProject = projectRef.current;
            if (!currentProject) return;
            try {
                const phaseIds = currentProject.phases.map(p => p.id);
                await axios.post(`${API_BASE_URL}/api/UserProjects/${projectId}/phases/reorder`, phaseIds);
            } catch (err) { console.error(err); }
        }, 100);
    };

    // FIX 1: Task Reorder Logic
    const onEndTaskDrag = async (phaseId) => {
        console.log("Task drag ended for phase:", phaseId);
        // Wait briefly for state to settle from handleSetTasksForPhase
        setTimeout(async () => {
            const currentProject = projectRef.current;
            if (!currentProject) return;

            const phase = currentProject.phases.find(p => p.id === phaseId);
            if (!phase || !phase.tasks) return;

            try {
                // Map tasks to get IDs in their current order
                const taskIds = phase.tasks.map(t => t.id);
                console.log("Saving new task order for phase", phaseId, "Task IDs:", taskIds);
                const payload = {
                    destinationPhaseId: phaseId,
                    taskIds: taskIds
                };
                await axios.post(`${API_BASE_URL}/api/UserProjects/phases/${phaseId}/tasks/reorder`, taskIds);
            } catch (err) {
                console.error("Failed to reorder tasks", err);
                showToast('error', 'Failed to save task order');
            }
        }, 100);
    };

    // --- 3. CRUD HANDLERS ---
    
    // PROJECT UPDATE
    const handleUpdateProject = async (updatedData) => {
        setIsLoading(true);
        try {
            // Build the DTO matching "UpdateProjectRequest" in C#
            const payload = {
                ProjectId: parseInt(projectId),
                Name: updatedData.name,
                Description: updatedData.description,
                Budget: updatedData.budget ? parseFloat(updatedData.budget) : null,
                Postcode: updatedData.postcode,
                Suburb: updatedData.suburb,
                MyState: updatedData.state, // Map 'state' from frontend to 'MyState' for backend
                Address: updatedData.address // Add Address
            };

            await axios.put(`${API_BASE_URL}/api/UserProjects/${projectId}`, payload);

            // Update local state immediately so UI reflects changes
            setProject(prev => ({ 
                ...prev, 
                name: updatedData.name, 
                description: updatedData.description,
                budget: payload.Budget,
                postcode: payload.Postcode,
                suburb: payload.Suburb,
                myState: payload.MyState, // Update both conventions to be safe
                state: payload.MyState,
                address: payload.Address // Update Address
            }));

            showToast('success', 'Project updated successfully');
            setIsEditProjectModalOpen(false);
        } catch (error) {
            console.error("Update failed:", error);
            showToast('error', 'Failed to update project details');
        } finally {
            setIsLoading(false);
        }
    };


    
    // PHASE CRUD
    const openAddPhaseModal = () => { setCurrentPhase(null); setIsPhaseModalOpen(true); };
    const openEditPhaseModal = (phase) => { setCurrentPhase(phase); setIsPhaseModalOpen(true); };
    const closePhaseModal = () => { setIsPhaseModalOpen(false); setCurrentPhase(null); };

    const handleSavePhase = async (phaseData) => {
        setIsLoading(true);
        try {
            const pId = parseInt(projectId);
            const userType = project.userType || "HomeUser"; 
            const ownerId = project.ownerUserId || 0;
            const payload = {
                PhaseId: phaseData.id || 0,
                ProjectId: pId,
                Name: phaseData.name,
                Description: phaseData.description || "", 
                Order: phaseData.order || 0,
                UserType: userType, 
                OwnerUserId: ownerId
            };

            if (phaseData.id) await axios.put(`${API_BASE_URL}/api/UserProjects/phases/${phaseData.id}`, payload);
            else await axios.post(`${API_BASE_URL}/api/UserProjects/${pId}/phases`, payload);
            
            showToast('success', phaseData.id ? 'Stage updated!' : 'Stage added!');
            closePhaseModal();
            fetchProject(); 
        } catch (err) { showToast('error', 'Failed to save stage.'); }
        finally { setIsLoading(false); }
    };

    const handleDeletePhase = async (phaseId) => {
        if (!window.confirm('Are you sure you want to delete this phase and all its tasks?')) return;
        setIsLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/UserProjects/phases/${phaseId}`);
            showToast('success', 'Stage deleted');
            fetchProject();
        } catch (err) { showToast('error', 'Failed to delete stage'); }
        finally { setIsLoading(false); }
    };

    // TASK CRUD
    const openAddTaskModal = (phaseId) => { setCurrentTask(null); setCurrentPhaseIdForTask(phaseId); setIsTaskModalOpen(true); };
    const openEditTaskModal = (task) => { setCurrentTask(task); setCurrentPhaseIdForTask(task.phaseId); setIsTaskModalOpen(true); };
    const closeTaskModal = () => { setIsTaskModalOpen(false); setCurrentTask(null); setCurrentPhaseIdForTask(null); };

    const handleSaveTask = async (taskData) => {
        setIsLoading(true);
        try {
            const payload = {
                TaskId: taskData.id || 0,
                PhaseId: taskData.phaseId,
                Name: taskData.name,
                Description: taskData.description || "",
                Status: taskData.status || "Pending",
                SuggestedTrade: taskData.suggestedTrade || "General",
                
                // FIX 3: Ensure ProfessionId is sent to the backend
                ProfessionId: taskData.professionId || null, 

                Order: taskData.order || 0,
                UserType: project.userType || "HomeUser",
                OwnerUserId: project.ownerUserId || 0
            };

            if (taskData.id) await axios.put(`${API_BASE_URL}/api/UserProjects/tasks/${taskData.id}`, payload);
            else await axios.post(`${API_BASE_URL}/api/UserProjects/phases/${taskData.phaseId}/tasks`, payload);
            
            showToast('success', taskData.id ? 'Task updated!' : 'Task added!');
            closeTaskModal();
            fetchProject();
        } catch (err) { showToast('error', 'Failed to save task'); }
        finally { setIsLoading(false); }
    };

    const handleDeleteTask = async (taskId, phaseId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        setIsLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/UserProjects/tasks/${taskId}`);
            showToast('success', 'Task deleted');
            fetchProject();
        } catch (err) { showToast('error', 'Failed to delete task'); }
        finally { setIsLoading(false); }
    };

    // --- 4. CONTRACTOR / JOB HANDLERS ---
    
    const openFindContractorsModal = (task) => { 
        setActiveTaskForContractors(task); 
        setIsFindContractorsModalOpen(true); 
    };

    const handlePostJob = () => { 
        setIsFindContractorsModalOpen(false); 
        if (!activeTaskForContractors) return;

        navigate('/job-detail-new', {
            state: {
                projectId: parseInt(projectId),
                phaseId: activeTaskForContractors.phaseId,
                taskId: activeTaskForContractors.id,
                taskName: activeTaskForContractors.name,
                taskDescription: activeTaskForContractors.description,
                professionId: activeTaskForContractors.profession?.id || activeTaskForContractors.profession?.professionId,
                professionName: activeTaskForContractors.profession?.name,
                postcode: project.postcode,
                suburb: project.suburb,
                projectState: project.state || project.mystate,
                projectAddress: project.address || project.Address || '',
                mode: 'broadcast'
            }
        });
    };

    const handleBrowsePros = () => { 
        setIsFindContractorsModalOpen(false); 
        if (activeTaskForContractors.jobId) {
            const profession = activeTaskForContractors.profession?.name || "";
            navigate(`/search?category=${profession}&inviteJobId=${activeTaskForContractors.jobId}`);
        } else {
            navigate('/job-detail-new', {
                state: {
                    projectId: parseInt(projectId),
                    phaseId: activeTaskForContractors.phaseId,
                    taskId: activeTaskForContractors.id,
                    taskName: activeTaskForContractors.name,
                    taskDescription: activeTaskForContractors.description,
                    professionId: activeTaskForContractors.profession?.professionId || activeTaskForContractors.profession?.id,
                    professionName: activeTaskForContractors.profession?.name,
                    postcode: project.postcode,
                    suburb: project.suburb,
                    projectState: project.state || project.mystate,
                    projectAddress: project.address || project.Address || '',
                    mode: 'invite'
                }
            });
        }
    };

    const handleInviteSent = () => {
        fetchProject();
    };

    // --- RENDER ---
    if (isLoading && !project) return <Loader2 />;
    if (error) return <div className="error-message">{error}</div>;
    if (!project) return <div className="error-message">Project not found.</div>;

    return (
        <>
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'projects'}/>
            
            <div className="pageContainer">
                <div className="project-manager">
                    {/* Header */}
                    <div className="project-header">
                        <div className="header-title-group">
                            <Link to="/projects" className="back-link"><ArrowLeft size={20} /></Link>
                            <div className="project-title-block">
                                <h2 style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                    {project.name}
                                    {/* FIX 2: Edit Button */}
                                    <button 
                                        className="icon-button" 
                                        onClick={() => setIsEditProjectModalOpen(true)}
                                        title="Edit Project Details"
                                    >
                                        <Edit size={18} />
                                    </button>
                                </h2>
                                <p>{project.description}</p>
                                {(project.address || project.Address || project.suburb) && (
                                    <p style={{display:'flex', alignItems:'center', gap:'6px', color:'#fff', fontSize:'0.9rem', margin:'4px 0 0 0'}}>
                                        <MapPin size={14} color="#d97706" />
                                        {project.address || project.Address || `${project.suburb || ''}${project.myState || project.state ? `, ${project.myState || project.state}` : ''} ${project.postcode || ''}`.trim()}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="project-actions">
                            <button onClick={() => setViewMode(v => v === 'horizontal' ? 'vertical' : 'horizontal')} className="switch-view-btn">
                                {viewMode === 'horizontal' ? <Rows size={18}/> : <LayoutTemplate size={18}/>}
                                {viewMode === 'horizontal' ? 'Vertical View' : 'Horizontal View'}
                            </button>
                        </div>
                    </div>

                    {/* --- VIEW MODES --- */}
                    
                    {viewMode === 'horizontal' ? (
                        /* ================== HORIZONTAL VIEW ================== */
                        <div className="horizontal-layout">
                            <ReactSortable
                                list={project.phases}
                                setList={handleSetPhases}
                                onEnd={onEndPhaseDrag}
                                group="phases"
                                animation={150}
                                ghostClass="sortable-ghost"
                                chosenClass="sortable-chosen"
                                handle=".drag-handle"
                                style={{ display: 'flex', gap: '1.5rem' }} 
                            >
                                {project.phases.map((phase) => (
                                    <HorizontalPhase
                                        key={phase.id}
                                        phase={phase}
                                        setTasksForPhase={handleSetTasksForPhase} 
                                        onAddTask={openAddTaskModal}
                                        onEditPhase={openEditPhaseModal}
                                        onDeletePhase={handleDeletePhase}
                                        onEditTask={openEditTaskModal}
                                        onDeleteTask={handleDeleteTask}
                                        onFindContractors={openFindContractorsModal}
                                        // FIX 1: Pass the handler
                                        onTaskDragEnd={onEndTaskDrag}
                                    />
                                ))}
                            </ReactSortable>
                            
                            <div className="add-phase-column">
                                <button id="add-phase-btn-column" onClick={openAddPhaseModal}>
                                    <Plus size={20} /> Add New Stage
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* ================== VERTICAL VIEW ================== */
                        <div className="plan-layout">
                            <div className="layout-lhs">
                                <h2>Your Project Plan</h2>
                                <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0', color: '#0066ff' }}>
                                    <ClipboardList size={120} strokeWidth={1} />
                                </div>
                                <p style={{ color: '#6c757d' }}>
                                    This plan outlines every step of your renovation. <br/>
                                    Find contractors or invite pros for each task.
                                </p>
                            </div>

                            <div className="layout-rhs">
                                <div className="phase-accordion">
                                    {project.phases.map((phase) => (
                                        <details key={phase.id} className="phase-card-accordion" open>
                                            <summary>
                                                <ChevronRight className="phase-chevron" />
                                                <div className="phase-info-accordion">
                                                    <h3>{phase.name}</h3>
                                                    <p>{phase.description}</p>
                                                </div>
                                                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                                                     <button onClick={(e) => { e.preventDefault(); openAddTaskModal(phase.id); }} className="icon-button"><Plus size={18} /></button>
                                                     <button onClick={(e) => { e.preventDefault(); openEditPhaseModal(phase); }} className="icon-button"><Edit size={16} /></button>
                                                     <button onClick={(e) => { e.preventDefault(); handleDeletePhase(phase.id); }} className="icon-button delete-button"><Trash2 size={16} /></button>
                                                </div>
                                            </summary>
                                            
                                            <div className="phase-tasks-container">
                                                {(phase.tasks || []).length === 0 && (
                                                    <div style={{color:'#adb5bd', fontSize:'0.9rem', fontStyle:'italic', padding:'10px'}}>
                                                        No tasks yet.
                                                    </div>
                                                )}
                                                
                                                <ReactSortable
                                                    list={phase.tasks || []}
                                                    setList={(newTasks) => handleSetTasksForPhase(phase.id, newTasks)}
                                                    group="vertical-tasks"
                                                    animation={150}
                                                    ghostClass="sortable-ghost"
                                                    handle=".drag-handle-vertical"
                                                    className="task-list-vertical"
                                                    style={{ minHeight: '10px' }}
                                                    // FIX 1: Add onEnd here too
                                                    onEnd={() => onEndTaskDrag(phase.id)}
                                                >
                                                    {(phase.tasks || []).map((task) => (
                                                        <VerticalTask
                                                            key={task.id}
                                                            task={task}
                                                            onEdit={openEditTaskModal}
                                                            onDelete={handleDeleteTask}
                                                            onFindContractors={openFindContractorsModal}
                                                        />
                                                    ))}
                                                </ReactSortable>
                                            </div>
                                        </details>
                                    ))}
                                </div>

                                <button id="add-phase-btn-row" onClick={openAddPhaseModal}>
                                    <Plus size={20} /> Add New Stage
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* --- MODALS --- */}
                {isEditProjectModalOpen && (
                    <EditProjectModal 
                        isOpen={isEditProjectModalOpen}
                        onClose={() => setIsEditProjectModalOpen(false)}
                        onSave={handleUpdateProject}
                        project={project}
                    />
                )}

                {isPhaseModalOpen && (
                    <PhaseModal 
                        isOpen={isPhaseModalOpen} 
                        onClose={closePhaseModal} 
                        onSave={handleSavePhase} 
                        phase={currentPhase} 
                    />
                )}
                
                {isTaskModalOpen && (
                    <TaskModal 
                        isOpen={isTaskModalOpen} 
                        onClose={closeTaskModal} 
                        onSave={handleSaveTask} 
                        task={currentTask} 
                        phaseId={currentPhaseIdForTask} 
                    />
                )}
                
                <FindContractorsModal 
                    isOpen={isFindContractorsModalOpen} 
                    onClose={() => setIsFindContractorsModalOpen(false)} 
                    onPostJob={handlePostJob} 
                    onBrowsePros={handleBrowsePros} 
                    taskName={activeTaskForContractors?.name} 
                />
                
                {isBrowseProsModalOpen && activeTaskForContractors && (
                    <BrowseProsModal 
                        isOpen={isBrowseProsModalOpen} 
                        onClose={() => setIsBrowseProsModalOpen(false)} 
                        trade={activeTaskForContractors.profession?.name || activeTaskForContractors.suggestedTrade} 
                        professionId={activeTaskForContractors.professionId} 
                        postcode={project.postcode} 
                        taskId={activeTaskForContractors.id} 
                        onInvite={handleInviteSent} 
                    />
                )}
            
            </div>
        </>
    );
};

export default ProjectDetail;
